import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import { GalleryDropzoneIcon } from "assets/icons";
import { Typography } from "new-components/typography/Typography";

interface DropzoneProps {
  onDrop: (files: File[]) => void;
  maxSize?: number;
  acceptedFormats?: { [key: string]: string[] };
  errorMessage: string | null;
  setErrorMessage: (message: string | null) => void;
  showSuccess?: boolean;
  successMessage?: string;
  maxFiles?: number;
  className?: string;
  error?: string | null;
  setError?: (error: string | null) => void;
  setLoading?: (loading: boolean) => void;
  loading?: boolean;
  uploadMessage?: string | null;
  onClose?: () => void; // New prop to trigger on close after success
  failedFiles?: File[]; // Files that failed upload, to be kept staged for retry
}

export function Dropzone({
  onDrop,
  loading,
  maxSize = 4 * 1024 * 1024, // 4MB default
  acceptedFormats = {
    "image/jpg": [".jpg"],
    "image/jpeg": [".jpeg"],
    "image/png": [".png"],
    "image/gif": [".gif"],
    "image/bmp": [".bmp"],
    "image/webp": [".webp"],
    "image/icon": [".ico"],
  },
  errorMessage,
  setErrorMessage,
  showSuccess = false,
  successMessage = "Image uploaded successfully!",
  maxFiles = 1,
  className = "",
  error,
  setError,
  uploadMessage,
  onClose, // New onClose prop
  failedFiles = [],
}: DropzoneProps) {
  const [showSuccessIcon, setShowSuccessIcon] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const stagedFilesCountRef = useRef(0);
  
  // Keep ref in sync for use in useDropzone callback (avoids stale closure)
  stagedFilesCountRef.current = stagedFiles.length;
  
  const isMultiFileMode = maxFiles > 1;

  const getFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

  const handleRemoveFile = (fileToRemove: File) => {
    setStagedFiles((prev) => prev.filter((file) => getFileKey(file) !== getFileKey(fileToRemove)));
  };

  const handleClearAll = () => {
    setStagedFiles([]);
    setErrorMessage(null);
    if (setError) {
      setError(null);
    }
  };

  const handleSubmit = () => {
    if (stagedFiles.length > 0) {
      onDrop(stagedFiles);
      setImageLoaded(true);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFormats,
    maxFiles,
    maxSize,
    multiple: isMultiFileMode,
    onDrop: (acceptedFiles) => {
      setErrorMessage(null);
      setShowSuccessIcon(false);

      if (acceptedFiles.length > 0) {
        if (isMultiFileMode) {
          // Use ref to get current count (avoids stale closure)
          const currentCount = stagedFilesCountRef.current;
          const totalAfterAdd = currentCount + acceptedFiles.length;
          
          if (totalAfterAdd > maxFiles) {
            const remaining = maxFiles - currentCount;
            if (remaining <= 0) {
              setErrorMessage(`Maximum ${maxFiles} files allowed. Remove some files to add more.`);
            } else {
              setErrorMessage(`Can only add ${remaining} more file${remaining !== 1 ? 's' : ''} (max ${maxFiles} total). You tried to add ${acceptedFiles.length}.`);
            }
            return; // Don't add any files
          }
          setStagedFiles((prev) => [...prev, ...acceptedFiles]);
        } else {
          // Single file mode - upload immediately
          onDrop(acceptedFiles);
          setImageLoaded(true);
        }
      }
    },
    onDropRejected: (rejections) => {
      // Check for too-many-files error first (applies to entire drop)
      const hasTooManyFiles = rejections.some(({ errors }) => 
        errors.some((error) => error.code === "too-many-files")
      );
      
      if (hasTooManyFiles) {
        setErrorMessage(`Too many files (max ${maxFiles})`);
        return;
      }
      
      // Collect individual file errors
      const errorMessages = rejections.flatMap(({ file, errors }) =>
        errors
          .filter((error) => error.code === "file-too-large" || error.code === "file-invalid-type")
          .map((error) => {
            if (error.code === "file-too-large") {
              return `"${file.name}" is too large (max ${maxSize / (1024 * 1024)}MB)`;
            }
            return `"${file.name}" has invalid file type`;
          })
      );
      
      setErrorMessage(errorMessages.join(". ") || "Some files were rejected.");
    },
  });

  useEffect(() => {
    if (showSuccess) {
      setShowSuccessIcon(true);
    }
  }, [showSuccess]);

  useEffect(() => {
    if (imageLoaded && onClose) {
      setShowSuccessIcon(true);
    }
  }, [imageLoaded, onClose]);

  useEffect(() => {
    if (showSuccessIcon && onClose) {
      setTimeout(() => {
        setImageLoaded(false);
        setShowSuccessIcon(false);
        onClose();
      }, 2000);
    }
  }, [showSuccessIcon, onClose]);

  // Clear staged files after successful upload
  useEffect(() => {
    if (showSuccessIcon) {
      setStagedFiles([]);
    }
  }, [showSuccessIcon]);

  // Keep failed files staged for retry when upload completes with failures
  useEffect(() => {
    if (!loading && failedFiles.length > 0) {
      setStagedFiles(failedFiles);
    }
  }, [loading, failedFiles]);

  const hasStagedFiles = isMultiFileMode && stagedFiles.length > 0;

  return (
    <Box
      className={`bg-white flex flex-col h-full max-h-[550px] rounded-lg border-2 border-gray-300 border-dashed ${className}`}
    >
      {/* Show progress while uploading */}
      {loading ? (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" flex={1}>
          <CircularProgress size={80} />
          <Typography variant="subtitle" mt={2}>
            {uploadMessage || "Uploading image..."}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Combined dropzone area with file list inside */}
          <div 
            className="flex flex-col flex-1 cursor-pointer w-full h-full overflow-hidden"
            {...getRootProps()}
          >
            <input {...getInputProps()} data-testid="dropzone-input" />
            {hasStagedFiles && !showSuccessIcon && (
              <Box 
                className="flex-1 overflow-auto p-2"
                onClick={(e) => e.stopPropagation()} // Prevent file browser when clicking list area
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} px={1}>
                  <Typography fontWeight="semibold">
                    {stagedFiles.length} file{stagedFiles.length !== 1 ? "s" : ""} selected
                    {stagedFiles.length < maxFiles && ` (max ${maxFiles})`}
                  </Typography>
                  <Button 
                    size="small" 
                    color="inherit" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearAll();
                    }}
                  >
                    Clear All
                  </Button>
                </Box>
                <Box className="max-h-[200px] overflow-auto">
                  {stagedFiles.map((file) => (
                    <Box 
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between"
                      px={1}
                      py={0.5}
                      className="hover:bg-gray-50 rounded"
                    >
                      <Box display="flex" alignItems="center" gap={1} overflow="hidden">
                        <InsertDriveFileIcon color="primary" fontSize="small" />
                        <Box overflow="hidden">
                          <Typography fontWeight="medium" className="truncate text-sm">
                            {file.name}
                          </Typography>
                          <Typography className="text-gray-500 text-xs">
                            {(file.size / 1024).toFixed(1)} KB
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(file);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Drop zone prompt area */}
            <Box 
              className={`flex flex-col justify-center items-center gap-[2px] ${hasStagedFiles ? "py-3 border-t border-gray-200 bg-gray-50" : "flex-1"}`}
            >
              {!showSuccessIcon && (
                  !hasStagedFiles ? (
                    <>
                      <GalleryDropzoneIcon color="#C8CDD3" />
                      <Typography fontWeight="semibold">Drag & Drop {maxFiles > 1 ? `up to ${maxFiles} images here` : "your image here"}</Typography>
                      <Typography fontWeight="bold" className="text-[#214FB3] cursor-pointer">or Browse</Typography>
                      <Typography fontWeight="semibold">(.jpeg/.jpg/.png/.gif/.bmp/.webp)</Typography>
                      <Typography fontWeight="semibold">Max size: {maxSize / (1024 * 1024)}MB</Typography>
                    </>
                  ) : stagedFiles.length < maxFiles && (
                    <>
                      <Typography fontWeight="semibold">+ Add more files</Typography>
                      <Typography fontWeight="bold" className="text-[#214FB3]">or Browse</Typography>
                      <Typography fontWeight="semibold" className="text-gray-500">
                        ({maxFiles - stagedFiles.length} more allowed)
                      </Typography>
                    </>
                  )
              )}
              {errorMessage && (
                <Typography color="red">
                  {errorMessage}
                </Typography>
              )}

              {error && (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" mt={2}>
                  <ErrorOutlineIcon color="error" style={{ fontSize: "80px" }} />
                  <Typography color="red">
                    {error}
                  </Typography>
                </Box>
              )}

              {/* Success Message with Success Icon */}
              {showSuccessIcon && !errorMessage && !error && (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" mt={2}>
                  <CheckCircleIcon color="success" style={{ fontSize: "80px" }} />
                  <Typography color="green">
                    {successMessage}
                  </Typography>
                </Box>
              )}
            </Box>
          </div>

          {/* Upload button - shown when files are staged */}
          {hasStagedFiles && !showSuccessIcon && (
            <Box display="flex" justifyContent="center" p={2} borderTop="1px solid #e0e0e0">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={stagedFiles.length === 0}
              >
                Upload {stagedFiles.length} File{stagedFiles.length !== 1 ? "s" : ""}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
